import "@/App.css";
import { useEffect, useState } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { ShiftFreezeProvider } from "@/context/ShiftFreezeContext";
import ShiftFreezeOverlay from "@/components/ShiftFreezeOverlay";
import ShiftFreezeWarning from "@/components/ShiftFreezeWarning";
import { api } from "@/lib/api";
import ProtectedRoute from "@/components/ProtectedRoute";
import Layout from "@/components/Layout";
import Login from "@/pages/Login";
import Dashboard from "@/pages/Dashboard";
import MasterExclusion from "@/pages/MasterExclusion";
import CampaignExclusion from "@/pages/CampaignExclusion";
import Leads from "@/pages/Leads";
import LeadForm from "@/pages/LeadForm";
import MyLeads from "@/pages/MyLeads";
import BulkUpload from "@/pages/BulkUpload";
import Admin from "@/pages/Admin";
import Leaves from "@/pages/Leaves";
import SurveyTake from "@/pages/SurveyTake";

// Send each role to its natural landing page
function RoleHome() {
  const { user } = useAuth();
  const [landing, setLanding] = useState(null);
  useEffect(() => {
    if (!user) return;
    if (user.role === "agent") { setLanding("/my-leads"); return; }
    // Admin/QA-base: check whether they actually have dashboard access (custom roles may restrict it)
    api.get("/permissions/effective")
      .then((r) => {
        const pp = r.data.page_permissions || {};
        if (pp.dashboard === false) setLanding("/my-leads");
        else setLanding("/");
      })
      .catch(() => setLanding("/"));
  }, [user?.email]); // eslint-disable-line react-hooks/exhaustive-deps
  if (user === null) return null;
  if (!user) return <Navigate to="/login" replace />;
  if (landing === null) return null;
  if (landing !== "/") return <Navigate to={landing} replace />;
  return <Dashboard />;
}

export default function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <ShiftFreezeProvider>
          <Routes>
            <Route path="/login" element={<Login />} />
            {/* Public survey page — anonymous respondents, no auth, no layout */}
            <Route path="/survey/:token" element={<SurveyTake />} />
            <Route path="/survey/i/:token" element={<SurveyTake invitationMode />} />
            {/* Any authenticated user */}
            <Route element={<ProtectedRoute />}>
              <Route element={<Layout />}>
                <Route path="/" element={<RoleHome />} />
              </Route>
            </Route>
            {/* Admin + QA — leads list + master/campaign tools */}
            <Route element={<ProtectedRoute roles={["admin", "qa"]} page="master_exclusion" />}>
              <Route element={<Layout />}>
                <Route path="/master-exclusion" element={<MasterExclusion />} />
              </Route>
            </Route>
            <Route element={<ProtectedRoute roles={["admin", "qa"]} page="campaign_exclusion" />}>
              <Route element={<Layout />}>
                <Route path="/campaign-exclusion" element={<CampaignExclusion />} />
              </Route>
            </Route>
            <Route element={<ProtectedRoute roles={["admin", "qa", "agent"]} page="leads" />}>
              <Route element={<Layout />}>
                <Route path="/leads" element={<Leads />} />
              </Route>
            </Route>
            {/* Admin + QA + Agent — edit a lead (agent only sees their assigned leads) */}
            <Route element={<ProtectedRoute roles={["admin", "qa", "agent"]} />}>
              <Route element={<Layout />}>
                <Route path="/leads/:id" element={<LeadForm />} />
              </Route>
            </Route>
            {/* All authenticated users — their personal queue across all assignment fields */}
            <Route element={<ProtectedRoute roles={["admin", "qa", "agent"]} />}>
              <Route element={<Layout />}>
                <Route path="/my-leads" element={<MyLeads />} />
              </Route>
            </Route>
            {/* Admin + Agent — create a lead */}
            <Route element={<ProtectedRoute roles={["admin", "agent"]} page="new_lead" />}>
              <Route element={<Layout />}>
                <Route path="/leads/new" element={<LeadForm />} />
              </Route>
            </Route>
            {/* Admin only */}
            <Route element={<ProtectedRoute roles={["admin", "agent", "qa"]} page="bulk_upload" />}>
              <Route element={<Layout />}>
                <Route path="/bulk-upload" element={<BulkUpload />} />
              </Route>
            </Route>
            <Route element={<ProtectedRoute roles={["admin", "qa", "agent"]} page="leaves" />}>
              <Route element={<Layout />}>
                <Route path="/leaves" element={<Leaves />} />
              </Route>
            </Route>
            <Route element={<ProtectedRoute roles={["admin", "qa", "agent"]} page={["admin", "admin_campaigns", "admin_agents", "admin_users", "admin_shifts", "admin_exclusion_lists", "admin_email_campaigns", "admin_productivity", "admin_productivity_view", "admin_reports", "admin_shift_schedules"]} />}>
              <Route element={<Layout />}>
                <Route path="/admin" element={<Admin />} />
              </Route>
            </Route>
          </Routes>
          <ShiftFreezeOverlay />
          <ShiftFreezeWarning />
          <Toaster />
          </ShiftFreezeProvider>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}
